/*
 * Copyright (c) 2024 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { groupBy, isEqual } from 'lodash';
import { PackageDtoModel, PackageSplitModel, PackageVoModel } from '@well/delivery-libs/packages/domain';
import { Maybe } from '@irian-ro/util-functions';

export class PackageUtils {
  static getGroupedPackagesDtos(packages: PackageDtoModel[]): PackageDtoModel[] {
    const packageGroups = groupBy(
      packages,
      (aPackage) =>
        aPackage.standardPackageDimensionUuid +
        ',' +
        aPackage.width +
        ',' +
        aPackage.length +
        ',' +
        aPackage.height +
        ',' +
        aPackage.weight +
        ',' +
        aPackage.type +
        ',' +
        aPackage.tags
    );
    return Object.values(packageGroups).map((identicalPackageGroup: PackageDtoModel[]) => {
      return identicalPackageGroup.reduce((prevItem, currentItem) => {
        return { ...prevItem, pieces: (prevItem.pieces || 0) + (currentItem.pieces || 0) };
      });
    });
  }

  static getGroupedPackagesVosByType(packages: PackageVoModel[]): PackageVoModel[] {
    return Object.values(
      packages.reduce<Record<string, PackageVoModel & { volume: number }>>((acc, item) => {
        const key = item.type ?? 'null';
        if (!acc[key]) {
          acc[key] = { ...item, count: 0, volume: 0, weight: 0 };
        }

        acc[key].count = (acc[key].count ?? 0) + (item.count ?? 0);
        acc[key].weight = (acc[key].weight ?? 0) + (item.count || 0) * (item.weight || 0);
        acc[key].volume =
          (acc[key].volume ?? 0) +
          ((item.count || 0) * (item.width || 0) * (item.height || 0) * (item.length || 0)) / 1000000;
        return acc;
      }, {})
    );
  }

  static groupPackagesByShape<
    T extends {
      standardPackageDimensionUuid: Maybe<string>;
      width: Maybe<number>;
      length: Maybe<number>;
      height: Maybe<number>;
      weight: Maybe<number>;
      type: Maybe<string>
      tags: string[];
    }
  >(packages: T[]): Record<string, T[]> {
    return groupBy(
      packages,
      (aPackage) =>
        (aPackage.standardPackageDimensionUuid || "" )+
        ',' +
        (aPackage.width || "" )+
        ',' +
        (aPackage.length || "" )+
        ',' +
        (aPackage.height || "" )+
        ',' +
        (aPackage.weight || "" )+
        ',' +
        (aPackage.type || "" )+
        ',' +
        aPackage.tags.join(',')
    );
  }

  static getGroupedPackagesVos(packages: PackageVoModel[]): PackageVoModel[] {
    const packageGroups = PackageUtils.groupPackagesByShape(packages);
    return Object.values(packageGroups).map((identicalPackageGroup: any) => {
      return identicalPackageGroup.reduce((prevItem: any, currentItem: any) => {
        return { ...prevItem, pieces: prevItem.pieces + currentItem.pieces };
      });
    });
  }

  static arePackagesEqual(package1: PackageDtoModel, package2: PackageDtoModel): boolean {
    return (
      package1.width === package2.width &&
      package1.length === package2.length &&
      package1.height === package2.height &&
      package1.weight === package2.weight &&
      package1.type === package2.type &&
      isEqual(package1.tags, package2.tags)
    );
  }

  static updateUngroupedPackages(displayPackages: PackageDtoModel[]): PackageDtoModel[] {
    const updatedPackages: PackageDtoModel[] = [];
    displayPackages.forEach((displayPackage) => {
      for (let i = 0; i < (displayPackage.pieces || 0); i++) {
        const ungroupedPackage = { ...displayPackage, pieces: 1 };
        updatedPackages.push(ungroupedPackage);
      }
    });
    return updatedPackages;
  }

  static getUniquePackagesDtoFromGroup <T extends PackageDtoModel | PackageSplitModel>(
    originalPackages: T[],
    groupedPackages: T[]
  ): T[] {
    //original packages are the packages that are not grouped witch means they have pieces = 1
    //grouped packages are the packages that are grouped witch means they have pieces >= 1

    const originalPackagesGroupedByShape = PackageUtils.groupPackagesByShape(originalPackages);
    const groupedPackagesGroupedByShape = PackageUtils.groupPackagesByShape(groupedPackages);

    const a = Object.keys(groupedPackagesGroupedByShape)
      .map((key) => {
        // if exists in original packages and packages count are equal then we keep the original package
        // if exists in original packages and packages count are less then original will kipp the first ones from original
        // if exists in original packages and packages count are more then original will take all from original and add new ones for the difference
        const groupedCount = groupedPackagesGroupedByShape[key].reduce((acc, curr) => acc + (curr.pieces || 0), 0);
        const genericPackage: T = {
          ...groupedPackagesGroupedByShape[key][0],
          id: null,
          uuid: null,
          pieces: 1
        };

        if (originalPackagesGroupedByShape[key]) {
          const originalCount = originalPackagesGroupedByShape[key].length;

          const difference = groupedCount - originalCount;

          if (difference === 0) {
            return [...originalPackagesGroupedByShape[key]];
          } else if (difference < 0) {
            return [...originalPackagesGroupedByShape[key].slice(-difference)];
          } else {
            const extraPackages = Array.from({ length: difference }, () => ({
              ...genericPackage,
              uuid: null,
              id: null
            }));
            return [...originalPackagesGroupedByShape[key], ...extraPackages];
          }
        } else {
          return [...Array.from({ length: groupedCount }, () => genericPackage)];
        }
      })
      .reduce((acc, curr) => [...acc, ...curr], []);

    return a || [];
  }

  static getMergedPackagesByCount(packages: PackageSplitModel[]): PackageSplitModel[] {
    const groupedPackages = groupBy(packages, (item) => {
      return [item.height, item.length, item.width, item.weight];
    });

    return Object.keys(groupedPackages).map((key): PackageSplitModel => {
      const mergedPackages = groupedPackages[key][0];
      mergedPackages.uuid = null;
      mergedPackages.pieces = (mergedPackages.pieces || 0) > 1 ? mergedPackages.pieces : groupedPackages[key].length;
      return mergedPackages;
    });
  }
}
